import React from 'react';
import { toast } from 'react-hot-toast';

import { ContactAvatar } from '@poseidon/components/Avatar';
import { cn } from '@poseidon/components/cn';
import { Button } from '@poseidon/components/Input2';
import { Popup } from '@poseidon/components/Popup';
import { Alerts, Users } from '@poseidon/components/StrokeIcons';

import { updateWithToast } from '@/clientOnlyUtils/clientUtils';
import { api } from '@/utils/api';

interface Props {
  teamId: string;
  skippedContactPublicIds: string[];
  onClose: () => void;
  /**
   * Called when the user clicks "Add these anyway". Receives the same
   * public ids the popup is displaying. Implementation is expected to
   * re-fire the original add mutation with `onlyNewPeople: false`
   * against just these contacts.
   */
  onAddAnyway: (contactPublicIds: string[]) => Promise<unknown>;
}

export function SkippedContactsPopup({ teamId, skippedContactPublicIds, onClose, onAddAnyway }: Props) {
  const displayQuery = api.contact.displayInfoByPublicIds.useQuery(
    { teamId, contactPublicIds: skippedContactPublicIds },
    { enabled: skippedContactPublicIds.length > 0 },
  );
  const contacts = displayQuery.data ?? [];

  const handleAddAnyway = () =>
    updateWithToast({
      loading: 'Adding...',
      success: `Added ${skippedContactPublicIds.length}`,
      mutationFnAsync: async () => {
        await onAddAnyway(skippedContactPublicIds);
        onClose();
      },
    });

  const handleKeepSkipped = () => {
    toast.success(`Kept ${skippedContactPublicIds.length} skipped`);
    onClose();
  };

  return (
    <Popup onClose={onClose}>
      <div className="flex flex-col gap-3 text-left max-w-lg w-full">
        <div className="flex items-center gap-2 text-neutral-900 dark:text-white">
          <Alerts.AlertCircle className="size-5" />
          <h2 className="text-lg font-bold">
            Skipped {skippedContactPublicIds.length} already-added{' '}
            {skippedContactPublicIds.length === 1 ? 'contact' : 'contacts'}
          </h2>
        </div>
        <p className="text-sm text-neutral-600 dark:text-gray-400">
          You've previously added these contacts to take action. They were skipped
          because you have "Only new people" selected.
        </p>
        <div
          className={cn(
            'flex flex-col gap-1 max-h-72 overflow-y-auto rounded-sm border',
            'border-neutral-200 dark:border-gray-700 p-2',
          )}>
          {contacts.map((c) => (
            <div key={c.contactPublicId} className="flex items-center gap-2 px-2 py-1">
              <ContactAvatar
                firstName={c.firstName}
                lastName={c.lastName}
                imageSrc={c.imageSrc}
                shape="circle"
                className="size-7"
              />
              <span className="text-sm text-neutral-800 dark:text-gray-200">
                {[c.firstName, c.lastName].filter((v) => v != null && v !== '').join(' ') ||
                  c.contactPublicId}
              </span>
            </div>
          ))}
          {contacts.length === 0 && displayQuery.isLoading && (
            <div className="px-2 py-1 text-sm text-neutral-500">Loading...</div>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button onClick={handleKeepSkipped} color="red" tone="outline" icon={<Users.UserPlus01 />}>
            Keep skipped
          </Button>
          <Button onClick={handleAddAnyway} color="blue" icon={<Users.Users01 />}>
            Add these anyway
          </Button>
        </div>
      </div>
    </Popup>
  );
}
